import * as Icons from "@nova/component-icons"
import { SidePanelLayerType, ToolName } from "@nova/constants"
import { Icon } from "@nova/protobuf"
import { RenamableCoordinate } from "features/data-management/workspace/TODO-put-in-package/RenamableCoordinate"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { selectAllIcons, selectIconById } from "features/entity-caching/icons/selectors"
import { selectActivatedFireId } from "features/map/control/selectors"
import { InspectorPanelIconLayer } from "features/map/inspector-panel/icons/InspectorPanelIconLayer"
import { zoomToIcon } from "features/map/inspector-panel/icons/module"
import {
    getGenericInspectorPanelComponent,
    getGenericLayerDataInspectorPanelComponent,
    getGenericMockableInspectorPanel,
    getGenericMockedInspectorPanelComponent,
} from "features/map/inspector-panel/inspector-panels/GenericInspectorPanel"
import { deleteIcon, updateIcon } from "features/pubsub/entityActions/cubes/icon"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { noop, pick } from "nodash"
import { setRenamingLayerId } from "panels/content/side-panel/slice.new"
import type { PluginRegistry, PluginSpec } from "plugins/plugin-registry"
import { useState } from "react"
import { IconTutorial } from "./IconTutorial"
import {
    getGenericInspectorPanelHeader,
    getGenericMockedInspectorPanelHeader,
} from "features/map/inspector-panel/inspector-panels/headers/GenericInspectorPanelHeader"
import { getGenericMockedInspectorPanelBody } from "features/map/inspector-panel/inspector-panels/bodies/GenericInspectorPanelBody"
import { IconTool } from "./IconTool"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { closeLayer } from "panels/content/side-panel/thunks"
import { IconUploadFlowProvider } from "./IconMultifileUpload"
import { InspectorPanelPlugin } from "features/map/inspector-panel/useInspectorPanel"
import { InspectorPanelIconData } from "features/map/inspector-panel/icons/InspectorPanelIconData"
import { getGenericInspectorPanelFooter } from "features/map/inspector-panel/inspector-panels/footers/GenericInspectorPanelFooter"
import { useUploadFlowContext } from "panels/content/side-panel/upload-flow/useUploadFlowContext"
import { InspectorPanelIconUpload } from "features/map/inspector-panel/icons/InspectorPanelIconUpload"
import { selectIconsThatLeftUploadPage } from "./IconSlice"

export function registerIconPlugin(registry: PluginRegistry) {
    registry
        .register({
            name: "icons",
            displayName: "Point",
            mockedDisplayName: "New Point",
            sidePanelLayerType: SidePanelLayerType.Icon,
            PrimaryIcon: Icons.PinDrop,
            updateAction: updateIcon,
            deleteAction: deleteIcon,
            confirmDelete: (icon) => !!icon.images.length,
            protobufMessage: Icon,
            selectAllEntities: selectAllIcons,
            selectSingleEntity: selectIconById,
            zoomTo: zoomToIcon,
            duplicate: () => undefined,
            search: (input, entity) =>
                entity.name.toLowerCase().includes(input.toLowerCase()) ? entity.name : undefined,
        })
        .implement("side-panel-layers-plugin")
        .implement("inspector-panel-plugin", (spec) => ({
            InspectorPanelComponent: getGenericMockableInspectorPanel({
                NonMocked: getNonMockedIconInspectorPanel(spec),
                Mocked: getGenericMockedInspectorPanelComponent({
                    Header: getGenericMockedInspectorPanelHeader(spec),
                    Body: getGenericMockedInspectorPanelBody({
                        RenderComponent: InspectorPanelIconLayer,
                    }),
                }),
            }),
            useSubtitles,
        }))
        .implement("toolbox-plugin", () => ({
            name: ToolName.Icon,
            shortcut: { letters: ["P"] },
            portalComponent: IconTool,
            TutorialComponent: IconTutorial,
        }))
}

function getNonMockedIconInspectorPanel(spec: PluginSpec<Icon>) {
    const InspectorPanelIconLayerNoTopBorder = ({ id }: { id?: string }) => {
        return <InspectorPanelIconLayer id={id} shouldShowTopBorder={false} />
    }
    const LayerDataInspectorPanel = getGenericLayerDataInspectorPanelComponent({
        spec,
        LayerComponent: InspectorPanelIconLayerNoTopBorder,
        DataComponent: InspectorPanelIconData,
    })
    const UploadingInspectorPanel = getGenericInspectorPanelComponent({
        spec,
        Header: getGenericInspectorPanelHeader(spec),
        Body: InspectorPanelIconUpload,
        Footer: getGenericInspectorPanelFooter(),
    })
    const InspectorPanelRenderComponent = ({
        id,
        plugin,
    }: {
        id: string
        plugin: InspectorPanelPlugin
    }) => {
        // Needs to be an additional component to access the context hook
        const { selectUploadsForSet } = useUploadFlowContext()
        const uploads = useAppSelector(selectUploadsForSet(id))
        const hasLeftUploadPage = useAppSelector(selectIconsThatLeftUploadPage())[id]
        if (uploads?.length && !hasLeftUploadPage) {
            return <UploadingInspectorPanel id={id} plugin={plugin} />
        }
        return <LayerDataInspectorPanel id={id} plugin={plugin} />
    }
    const NonMockedIconInspectorPanel = ({
        id,
        plugin,
    }: {
        id: string
        plugin: InspectorPanelPlugin
    }) => {
        const dispatch = useAppDispatch()
        const workspaceId = useAppSelector(selectActiveWorkspaceId())
        const projectId = useAppSelector(selectActivatedFireId)
        if (!id || !projectId || !workspaceId) return null
        return (
            <IconUploadFlowProvider
                id={id}
                blurData={() => dispatch(closeLayer())}
                fireId={projectId}
                portfolioId={workspaceId}
                processedActionHandler={{
                    title: "Zoom To",
                    icon: () => <Icons.Center className="!h-[1.5em] !mb-0 scale-75" />,
                    handler: noop,
                }}
            >
                <InspectorPanelRenderComponent id={id} plugin={plugin} />
            </IconUploadFlowProvider>
        )
    }
    return NonMockedIconInspectorPanel
}

function useSubtitles(id: string) {
    const dispatch = useAppDispatch()
    const icon = useAppSelector(selectIconById(id))
    const [isRenaming, setIsRenaming] = useState(false)
    const projectId = useAppSelector(selectActivatedFireId)
    const { isEditor } = useWorkspacePermissions(projectId || "")
    return (
        <RenamableCoordinate
            id="inspector-coordinate"
            Tag="small"
            coordinate={pick(icon, ["longitude", "latitude"])}
            isRenaming={isRenaming}
            onClick={() => {
                if (!isEditor) return
                setIsRenaming(true)
                dispatch(setRenamingLayerId({ id: undefined }))
            }}
            className={isEditor ? "cursor-text" : undefined}
            shouldHighlightOnHover={isEditor}
            onCancelRenaming={() => setIsRenaming(false)}
            onConfirmRenaming={(_, { longitude, latitude }) => {
                dispatch(updateIcon({ id, updates: { longitude, latitude } }))
                setIsRenaming(false)
            }}
        />
    )
}
